import React, { useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useAuthContext, useNotificationContext } from "../../context";
import { MdClose } from "react-icons/md";
import { FaHome, FaEnvelope, FaSearch, FaUser, FaSignOutAlt, FaStar, FaEye, FaUsers, FaBullhorn, FaHeart } from 'react-icons/fa';

export default function Sidebar({ isOpen, onClose, onSearch }) {
  const { user, logoutHandler } = useAuthContext();
  const { chatUnreadCount } = useNotificationContext();
  const navigate = useNavigate();
  const sidebarRef = useRef(null);

 // const isGold = user?.membership_type === "gold" && user?.membership_status;

  // close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleLogout = () => {
    Swal.fire({
      title: "Log out?",
      text: "You will be signed out of your account.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, log me out",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        popup: "rounded-xl shadow-xl p-4 max-w-sm",
        confirmButton:
          "bg-red-500 text-white px-4 py-2 rounded-full hover:bg-red-600 focus:outline-none",
        cancelButton:
          "bg-gray-100 text-gray-800 px-4 py-2 rounded-full hover:bg-gray-200 focus:outline-none",
        title: "text-lg font-semibold text-gray-800",
        htmlContainer: "text-sm text-gray-600",
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        logoutHandler();
        scrollToTop();
        navigate("/login", { replace: true });
      }
    });
  };

  const comingSoon = () => {
    Swal.fire({
      icon: "info",
      title: "Coming soon!",
      text: "This feature is on its way—stay tuned!",
      confirmButtonText: "OK",
      customClass: {
        popup: "rounded-xl shadow-xl p-4 max-w-sm",
        confirmButton:
          "bg-blue-500 text-white px-4 py-2 rounded-full hover:bg-blue-600 focus:outline-none",
      },
      buttonsStyling: false,
    });
  };

  const normalizeGender = (value) => {
    if (value === "Man") return "Male";
    if (value === "Woman") return "Female";
    return value;
  }; 

  const scrollToTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  // wrapper click should not close if clicking inside panel
  return (
    <div className="fixed inset-0 z-50 flex md:hidden pt-6">
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* sliding panel */}
      <div
        ref={sidebarRef}
        className={`relative ml-auto w-64 h-full bg-white shadow-xl p-6
                    transform transition-transform duration-300
                    ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-700 focus:outline-none"
        >
          <MdClose size={24} />
        </button>

        {/* nav */}
        <nav className="mt-8 flex flex-col space-y-6 text-gray-800">
          <div>
            <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">
              Activity
            </h4>
            <ul className="space-y-1">
              {/* Home always */}
              <li>
                <Link to="/home"  onClick={() => {
                  scrollToTop();
                  onClose();
                  }}
                  className="hover:text-red-500 flex items-center space-x-2">
                  <FaHome />
                  <span>Home</span>
                </Link>
              </li>

              {/* Inbox */}
              <li className="relative">
                <button
                  onClick={() => {
                    user ? navigate("/inbox") : navigate("/login");
                    scrollToTop();
                    onClose();
                  }}
                  className="w-full text-left hover:text-red-500 flex items-center space-x-2"
                >
                  <FaEnvelope />
                  <span>Inbox</span>
                </button>
                {user && chatUnreadCount > 0 && (
                  <span className="absolute top-0 right-0 -mt-1 -mr-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    !
                  </span>
                )}
              </li>

              {/* Search always */}
              <li>
                <button
                  onClick={() => {
                    onSearch();
                    onClose();
                  }}
                  className="w-full text-left hover:text-red-500 flex items-center space-x-2"
                >
                  <FaSearch />
                  <span>Search</span>
                </button>
              </li>

               {/* Matches */}
               <button
               onClick={() => {
                 if (!user) {
                   navigate("/login");
                   scrollToTop();
                   onClose();
                 } else {
                   const matchesQuery = {
                     ageRange: [18, 99],
                     gender: normalizeGender(user.gender),
                     lookingFor: normalizeGender(user.interestedIn),
                     country: "",
                     state: "",
                     city: ""
                   };
             
                   localStorage.setItem("searchQuery", JSON.stringify(matchesQuery));
                   navigate("/search", { state: matchesQuery });
                   onClose();
                   scrollToTop();
                 }
               }}
               className="w-full text-left flex items-center gap-2 hover:text-red-500"
             >
               <FaUsers />
               Matches
             </button>

              <li>
                <button
                  onClick={() => {
                    if (!user) {
                      navigate("/login");
                      scrollToTop();
                    } else {
                      navigate("/profile");
                      scrollToTop();
                    }
                    onClose();
                  }}
                  className="w-full text-left hover:text-red-500 flex items-center space-x-2"
                >
                  <FaUser />
                  <span>My Profile</span>
                </button>
              </li>

              <li>
                <button
                  onClick={() => {
                    if (!user) {
                      navigate("/login");
                      scrollToTop();
                    } else {
                      navigate("/dashboard");
                      scrollToTop();
                    }
                    onClose();
                  }}
                  className="w-full text-left hover:text-red-500 flex items-center space-x-2"
                >
                  <FaUsers />
                  <span>Dashboard</span>
                </button>
              </li>

              {/* Favourites (login+gold) */}
              <li>
                <button
                  onClick={() => {
                    if (!user) {
                      navigate("/login");
                      scrollToTop();
                    } else {
                      comingSoon();
                    }
                    onClose();
                  }}
                  className="w-full text-left hover:text-red-500 flex items-center space-x-2"
                >
                  <FaStar />
                  <span>Favourites</span>
                </button>
              </li>

              {/* Who's Viewed Me (login+gold) */}
              <li>
                <button
                  onClick={() => {
                    if (!user) {
                      navigate("/login");
                      scrollToTop();
                    } else {
                      comingSoon();
                    }
                    onClose();
                  }}
                  className="w-full text-left hover:text-red-500 flex items-center space-x-2"
                >
                  <FaEye />
                  <span>Who's Viewed Me</span>
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">
              Community
            </h4>
            <ul className="space-y-1">
              {/** All community items require login */}
              {[
                { label: "Events", icon: <FaBullhorn /> },
                { label: "Message board", icon: <FaUsers /> },
                { label: "Success stories", icon: <FaHeart /> },
              ].map(({ label, icon }) => (
                <li key={label}>
                  <button
                    onClick={() => {
                      if (!user) {
                        navigate("/login");
                        scrollToTop();
                      } else {
                        comingSoon();
                      }
                      onClose();
                    }}
                    className="w-full text-left hover:text-red-500 flex items-center space-x-2"
                  >
                    {icon}
                    <span>{label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Top buttons */}
        <div className="space-y-3 mt-8">
          {!user ? (
            <>
              <Link
                to="/signup"
                onClick={() => {
                  scrollToTop();
                  onClose();
                }}
                className="w-full text-center py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center space-x-2"
              >
                <FaUser />
                <span>Register now</span>
              </Link>
              <Link
                to="/login"
                onClick={() => {
                  scrollToTop();
                  onClose();
                }}
                className="w-full text-center py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center space-x-2"
              >
                <FaSignOutAlt />
                <span>Log in</span>
              </Link>
            </>
          ) : (
            <button
              onClick={() => {
                handleLogout();
                onClose();
              }}
              className="w-full py-2 bg-red-500 text-white rounded hover:bg-red-600 flex items-center justify-center space-x-2"
            >
              <FaSignOutAlt />
              <span>Logout</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
